import { Duration } from '@/utilities/types/duration';
import { defineStore } from 'pinia';

export enum PatientGender {
    Male = 'male',
    Female = 'female',
}

export const usePatientStore = defineStore({
    id: 'patientStore',
    
    state()
    {
        return {
            // Required information
            firstName: "",
            lastName: "",
            birthDate: null as Date|null,
            gender: null as PatientGender|null,

            // Extra information
            identifier: "",
            stayNumber: "",
            address: "",
            city: "",
            postcode: "",
            state: "",
            country: "",

            // Physician in charge
            physicianTitle: "",
            physicianFirstName: "",
            physicianLastName: "",
            physicianAffiliation: "",
            physicianAddress: "",
            physicianPhone: "",
            physicianCity: "",
            physicianPostcode: "",
            physicianState: "",
            physicianCountry: "",
        };
    },

    getters: {
        fullName(): string
        {
            return `${this.firstName} ${this.lastName}`
        },

        areRequiredFieldsFilled(): boolean
        {
            return (
                this.firstName.length > 0 &&
                this.lastName.length > 0 &&
                this.birthDate !== null &&
                this.gender !== null
            );
        },

        numericGender(): number
        {
            switch (this.gender) {
                case PatientGender.Female:
                    return 0;

                case PatientGender.Male:
                    return 1;

                default:
                    return 0.5;
            }
        }
    },

    actions: {
        getLastBirthday(atDate: Date = new Date()): Date|null
        {
            if (this.birthDate === null) {
                return null;
            }

            const tmpBirthDate = new Date(this.birthDate.getTime());
            const thisYearBirthday = new Date(tmpBirthDate.setFullYear(atDate.getFullYear()));

            if (thisYearBirthday <= atDate) {
                return thisYearBirthday;
            }

            return new Date(tmpBirthDate.setFullYear(atDate.getFullYear() - 1));
        },

        getAgeInYears(atDate: Date = new Date()): number|null
        {
            const lastBirthday = this.getLastBirthday(atDate);

            if (this.birthDate === null || lastBirthday === null) {
                return null;
            }

            return lastBirthday.getFullYear() - this.birthDate.getFullYear();
        },

        getAgeInMonths(atDate: Date = new Date()): number|null
        {
            if (this.birthDate === null) {
                return null;
            }

            return (atDate.getFullYear() * 12 + atDate.getMonth()) - (this.birthDate.getFullYear() * 12 + this.birthDate.getMonth());
        },

        getAgeInWeeks(atDate: Date = new Date()): number|null
        {
            const ageInDays = this.getAgeInDays(atDate);

            return (ageInDays === null) ? null : Math.floor(ageInDays / 7);
        },

        getAgeInDays(atDate: Date = new Date()): number|null
        {
            if (this.birthDate === null) {
                return null;
            }

            return Math.floor((atDate.getTime() - this.birthDate.getTime()) / Duration.fromDays(1).milliseconds);
        },

    },
});
